import { Box, Card, CardContent, Grid, Typography } from '@mui/material';

import { useThemeContext } from '../../contexts/ThemeContext.js';
import { OrganizationType } from '../../types/organization';

type OrgNameDescriptionVariant = 'banner' | 'card' | 'both';

interface OrgNameDescriptionProps {
  org: OrganizationType;
  variant?: OrgNameDescriptionVariant;
}

export function OrgBanner({ org, description, sx }: { org: OrganizationType; description: string; sx?: any }): JSX.Element {
  const { theme } = useThemeContext();
  return (
    <Box
      sx={{
        backgroundColor: theme.layout.defaults.backgroundColor,
        borderRadius: '16px',
        padding: '12px',
        ...sx,
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 700,
          color: 'text.primary',
        }}
      >
        {org.name}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          mt: 0.5,
        }}
      >
        {description}
      </Typography>
    </Box>
  );
}

export function OrgDetailsCard({
  org,
  hasDescription,
}: {
  org: OrganizationType;
  hasDescription: boolean;
}): JSX.Element {
  return (
    <Grid container spacing={2} mt={2}>
      <Grid item xs={12} md={6}>
        <Card sx={{ mt: 2 }}>
          <CardContent>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
              Organization Details
            </Typography>

            <Typography variant="body1" sx={{ mb: 0.5 }}>
              Name: {org.name}
            </Typography>

            {hasDescription && org.description?.trim() ? (
              <Typography variant="body1" sx={{ mb: 2 }}>
                Description: {org.description.trim()}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                No description provided.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
}

export default function OrgNameDescription({ org, variant = 'banner' }: OrgNameDescriptionProps): JSX.Element {
  const trimmedDescription = org.description?.trim() ?? '';
  const hasDescription = Boolean(trimmedDescription);
  const description = hasDescription ? trimmedDescription : 'No description provided';
  const showBanner = variant === 'banner' || variant === 'both';
  const showCard = variant === 'card' || variant === 'both';
  const contentGap = showBanner && showCard ? '16px' : 0;

  return (
    <Box sx={{ display: 'grid', gap: contentGap }}>
      {showBanner ? <OrgBanner org={org} description={description} /> : null}
      {showCard ? <OrgDetailsCard org={org} hasDescription={hasDescription} /> : null}
    </Box>
  );
}
